import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar } from "react-multi-date-picker";
import { toast } from "react-toastify";
import { DndContext, closestCenter, useDraggable, useDroppable, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { FaArrowLeft, FaRegClock } from "react-icons/fa6";
import { FiSave, FiRotateCcw } from "react-icons/fi";
import { MdOutlineLocationOn, MdOutlinePeopleAlt } from "react-icons/md";
import Handler from "../../../Helpers/Handler";
import { formatDate, textCapitalize } from "../../../Helpers/Utils";
import { Skeleton } from "../../../Components/Needed";
import { listBackPath } from "../../../Helpers/useListParams";

const UNASSIGNED = "leave";

const startOfDay = (value) => {
    const date = new Date(value);
    date.setHours(0, 0, 0, 0);
    return date;
};

const StaffCard = ({ staff, disabled, extraShift, extraOptions, onExtraChange }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: staff.staffId, disabled, });
    const style = {
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        cursor: disabled ? "default" : "grab",
        opacity: isDragging ? 0.4 : 1,
    };
    const stop = (event) => event.stopPropagation();
    return (
        <div ref={setNodeRef} style={style} {...listeners} {...attributes} className="shift-card">
            <div className="shift-card-avatar">{(staff.name || "?").charAt(0).toUpperCase()}</div>
            <div className="min-w-0 flex-grow-1">
                <div className="shift-card-name text-capitalize">{textCapitalize(staff.name)}</div>
                <div className="shift-card-meta">
                    {staff.roleName ? <span className="text-capitalize">{textCapitalize(staff.roleName)}</span> : null}
                    {staff.departmentName ? <span className="text-capitalize"> · {textCapitalize(staff.departmentName)}</span> : null}
                </div>
                {extraOptions ? (
                    <select
                        className="form-select form-select-sm shift-card-extra"
                        value={extraShift || ""}
                        disabled={disabled}
                        onPointerDown={stop}
                        onMouseDown={stop}
                        onClick={stop}
                        onChange={(event) => onExtraChange(staff.staffId, event.target.value)}
                    >
                        <option value="">+ Add a second shift</option>
                        {extraOptions.map((shift) => <option key={shift.value} value={shift.value}>{shift.name}</option>)}
                    </select>
                ) : null}
            </div>
        </div>
    );
};

const Column = ({ id, title, subtitle, count, tone, children }) => {
    const { setNodeRef, isOver } = useDroppable({ id });
    return (
        <div ref={setNodeRef} className={`shift-col ${tone || ""} ${isOver ? "is-over" : ""}`}>
            <div className="shift-col-head">
                <div className="min-w-0">
                    <div className="shift-col-title">{title}</div>
                    {subtitle ? <div className="shift-col-sub">{subtitle}</div> : null}
                </div>
                <span className="shift-col-count">{count}</span>
            </div>
            <div className="shift-col-body">{children}</div>
        </div>
    );
};

const ShiftBoard = () => {
    const navigate = useNavigate();
    const canAssign = true;
    const [hubs, setHubs] = useState([]);
    const [shiftTypes, setShiftTypes] = useState([]);
    const [hubId, setHubId] = useState("");
    const [shiftDate, setShiftDate] = useState(() => { const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1); return startOfDay(tomorrow); });
    const [roster, setRoster] = useState([]);
    const [assignments, setAssignments] = useState({});
    const [baseline, setBaseline] = useState({});
    const [extraAssignments, setExtraAssignments] = useState({});
    const [extraBaseline, setExtraBaseline] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState("");

    useEffect(() => {
        (async () => {
            const [hubResponse, shiftResponse] = await Promise.all([Handler({ method: "GET", url: "/admin/hub/active" }), Handler({ method: "GET", url: "/admin/staff/shift/list" }),]);
            const hubList = hubResponse?.success ? hubResponse?.data?.data || [] : [];
            setHubs(hubList);
            setShiftTypes(shiftResponse?.success ? shiftResponse?.data?.data || [] : []);
            if (hubList.length) setHubId((prev) => prev || hubList[0].hubId);
        })();
    }, []);

    const loadRoster = useCallback(async () => {
        if (!hubId || !shiftDate) return;
        setLoading(true);
        const response = await Handler({
            method: "post",
            url: "/admin/staff/schedule",
            data: { hubId, shiftDate: new Date(shiftDate).toISOString() },
        });
        const list = response?.success ? response?.data?.data?.shifts || [] : [];
        setRoster(list);
        const map = {};
        const extraMap = {};
        list.forEach((staff) => {
            const shifts = staff.shifts?.length ? staff.shifts : (staff.shift && staff.shift !== UNASSIGNED ? [staff.shift] : []);
            map[staff.staffId] = shifts[0] || UNASSIGNED;
            extraMap[staff.staffId] = shifts[1] || "";
        });
        setAssignments(map);
        setBaseline(map);
        setExtraAssignments(extraMap);
        setExtraBaseline(extraMap);
        setLoading(false);
    }, [hubId, shiftDate]);

    useEffect(() => { loadRoster(); }, [loadRoster]);

    const isBeforeTomorrow = useMemo(() => {
        const tomorrow = startOfDay(new Date());
        tomorrow.setDate(tomorrow.getDate() + 1);

        return startOfDay(shiftDate) < tomorrow;
    }, [shiftDate]);

    const editable = canAssign && !isBeforeTomorrow;

    const dirty = useMemo(
        () => Object.keys(assignments).some((staffId) => assignments[staffId] !== baseline[staffId])
            || Object.keys(extraAssignments).some((staffId) => (extraAssignments[staffId] || "") !== (extraBaseline[staffId] || "")),
        [assignments, baseline, extraAssignments, extraBaseline]
    );

    const visible = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return roster;
        return roster.filter((staff) =>
            (staff.name || "").toLowerCase().includes(term)
            || (staff.staffId || "").toLowerCase().includes(term)
            || (staff.roleName || "").toLowerCase().includes(term)
            || (staff.departmentName || "").toLowerCase().includes(term));
    }, [roster, search]);

    const inColumn = (key) => visible.filter((staff) => (assignments[staff.staffId] || UNASSIGNED) === key);

    const extraOptionsFor = (staffId) => shiftTypes.filter((item) => item.value !== assignments[staffId]);

    const onExtraChange = (staffId, value) => setExtraAssignments((prev) => ({ ...prev, [staffId]: value }));

    const onDragEnd = ({ active, over }) => {
        if (!over || !editable) return;
        const target = over.id;
        const valid = [UNASSIGNED, ...shiftTypes.map((item) => item.value)];
        if (!valid.includes(target)) return;
        setAssignments((prev) => ({ ...prev, [active.id]: target }));
        setExtraAssignments((prev) => (
            target === UNASSIGNED || prev[active.id] === target
                ? { ...prev, [active.id]: "" }
                : prev
        ));
    };

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

    const reset = () => { setAssignments(baseline); setExtraAssignments(extraBaseline); };

    const save = async () => {
        const changedIds = [
            ...Object.keys(assignments).filter((staffId) => assignments[staffId] !== baseline[staffId]),
            ...Object.keys(extraAssignments).filter((staffId) => (extraAssignments[staffId] || "") !== (extraBaseline[staffId] || "")),
        ].filter((value, index, all) => all.indexOf(value) === index);
        const changed = changedIds.flatMap((staffId) => {
            const values = [assignments[staffId], extraAssignments[staffId]].filter((value) => value && value !== UNASSIGNED);
            return values.length ? values.map((shift) => ({ staffId, shift })) : [{ staffId, shift: UNASSIGNED }];
        });
        if (!changed.length) {
            toast.info("Nothing to save");
            return;
        }
        setSaving(true);
        const response = await Handler({
            method: "post",
            url: "/admin/staff/schedule/change",
            data: { hubId, shiftDate: new Date(shiftDate).toISOString(), shifts: changed },
        });
        if (response?.success) {
            toast.success(response?.data?.message || "Schedule updated");
            await loadRoster();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update the schedule"));
        }
        setSaving(false);
    };

    const assignedCount = roster.filter((staff) => (assignments[staff.staffId] || UNASSIGNED) !== UNASSIGNED).length;

    return (
        <div className="container-fluid">
            <button type="button" className="btn btn-link text-decoration-none px-0 mb-2 d-inline-flex align-items-center gap-2" onClick={() => navigate(listBackPath("/staff"))}>
                <FaArrowLeft /> Back to staff
            </button>

            <div className="card border-0 shadow-sm rounded-4 mb-3 shift-hero">
                <div className="card-body p-4">
                    <div className="d-flex flex-wrap align-items-start gap-3">
                        <div className="flex-grow-1 min-w-0">
                            <h3 className="fw-bold mb-1 d-flex align-items-center gap-2"><FaRegClock className="text-primary" /> Shift allocation</h3>
                            <p className="text-muted mb-0 small">Drag a person onto a shift to allocate them. Anyone left in Unassigned has no shift that day. Someone covering a double shift can pick a second shift from their card.</p>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <span className="shift-kpi"><MdOutlinePeopleAlt /> {assignedCount} / {roster.length} allocated</span>
                        </div>
                    </div>

                    <div className="row g-3 mt-1 align-items-end">
                        <div className="col-12 col-xl-3">
                            <label className="form-label small fw-semibold d-flex align-items-center gap-1"><MdOutlineLocationOn /> Hub</label>
                            <select className="form-select" value={hubId} onChange={(e) => setHubId(e.target.value)}>
                                <option value="">Select hub</option>
                                {hubs.map((hub) => (
                                    <option key={hub.hubId} value={hub.hubId}>
                                        {hub.hubId}{hub.area ? ` - ${hub.area}` : ""}{hub.city ? `, ${hub.city}` : ""}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-12">
                            <div> <label className="form-label small fw-semibold mb-0">Date: </label><label className="fw-semibold ms-1">{formatDate(shiftDate)}</label> <label>{isBeforeTomorrow ? <label className="shift-warn small mt-1">(Past date - read only)</label> : null}</label> </div>
                            <div className="mb-2"></div>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <Calendar className="shadow-none shift-calendar" value={shiftDate} onChange={(date) => setShiftDate(date?.toDate ? date.toDate() : new Date(date))} />
                            </div>
                        </div>
                        {/* <div className="col-12 col-xl-4">
                            <label className="form-label small fw-semibold">Find someone</label>
                            <div className="role-search">
                                <FiSearch />
                                <input type="text" className="form-control" placeholder="Name, ID, role or department..." value={search} onChange={(e) => setSearch(e.target.value)} />
                            </div>
                        </div> */}
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="row g-3">
                    {[0, 1, 2, 3].map((i) => <div key={i} className="col-12 col-md-6 col-xl-3"><Skeleton height={300} className="rounded-4" /></div>)}
                </div>
            ) : !hubId ? (
                <div className="card border-0 shadow-sm rounded-4"><div className="card-body text-center py-5 text-muted">Select a hub to see its allocation.</div></div>
            ) : !roster.length ? (
                <div className="card border-0 shadow-sm rounded-4"><div className="card-body text-center py-5 text-muted">No staff are assigned to this hub.</div></div>
            ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                    <div className="row g-3">
                        <div className="col-12 col-xl-3">
                            <Column
                                id={UNASSIGNED}
                                title="Unassigned"
                                subtitle="No shift on this date"
                                tone="is-pool"
                                count={inColumn(UNASSIGNED).length}
                            >
                                {inColumn(UNASSIGNED).length
                                    ? inColumn(UNASSIGNED).map((staff) => <StaffCard key={staff.staffId} staff={staff} disabled={!editable} />)
                                    : <div className="shift-empty">Everyone is allocated</div>}
                            </Column>
                        </div>
                        <div className="col-12 col-xl-9">
                            <div className="row g-3">
                                {shiftTypes.map((shift) => (
                                    <div key={shift.value} className="col-12 col-md-6 col-xxl-4">
                                        <Column id={shift.value} title={shift.name} subtitle={shift.value} count={inColumn(shift.value).length}>
                                            {inColumn(shift.value).length ? inColumn(shift.value).map((staff) => (
                                                <StaffCard
                                                    key={staff.staffId}
                                                    staff={staff}
                                                    disabled={!editable}
                                                    extraShift={extraAssignments[staff.staffId]}
                                                    extraOptions={editable ? extraOptionsFor(staff.staffId) : null}
                                                    onExtraChange={onExtraChange}
                                                />
                                            )) : <div className="shift-empty">{editable ? "Drop staff here" : "Nobody allocated"}</div>}
                                        </Column>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </DndContext>
            )}

            {editable && dirty ? (
                <div className="shift-savebar">
                    <span className="small">You have unsaved changes</span>
                    <div className="d-flex gap-2">
                        <button type="button" className="btn btn-sm btn-light border d-inline-flex align-items-center gap-2" onClick={reset} disabled={saving}>
                            <FiRotateCcw /> Reset
                        </button>
                        <button type="button" className="btn btn-sm btn-primary d-inline-flex align-items-center gap-2" onClick={save} disabled={saving}>
                            <FiSave /> {saving ? "Saving..." : "Save schedule"}
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default ShiftBoard;
